import { gql } from '@/shared/graphql/generated/explorer';
import {
  type AccountWithPossibleValidatorFragment,
  type AccountFundingEventFragment,
  type AuthorityMembershipWithValidatorFragment,
  type EpochFragment,
} from '@/shared/graphql/generated/explorer/graphql';
import { type TokenCast } from '@/shared/utils/TokenAmount';

gql(/* GraphQL */ `
  fragment AccountFundingEvent on AccountFundingEvent {
    id
    amount
    valueUsd
    type
    txHash
    rebalanceCounterparty: accountByRebalanceCounterpartyId {
      idSs58
    }
    event: eventByEventId {
      indexInBlock
      blockId
      block: blockByBlockId {
        timestamp
      }
    }
    account: accountByAccountId {
      idSs58
      role
      alias
    }
    swapRequest: swapRequestBySwapRequestId {
      nativeId
    }
    accountChannel: accountCreationDepositChannelByAccountCreationDepositChannelId {
      issuedBlockId
      chain
      channelId
    }
  }
`);

export const accountFundingEventCastFields = ['amount'] as const;
export type CastAccountFundingEventFragment = TokenCast<
  AccountFundingEventFragment,
  (typeof accountFundingEventCastFields)[number]
>;

type ValidatorInfo = AccountWithPossibleValidatorFragment['validator'];
export type AccountWithValidator = AccountWithPossibleValidatorFragment & {
  validator: NonNullable<ValidatorInfo>;
};

gql(/* GraphQL */ `
  fragment Epoch on Epoch {
    id
    bond
    totalBonded
    maxAuthoritySetSize
    startBlock: blockByStartBlockId {
      id
      hash
      timestamp
    }
    endBlock: blockByEndBlockId {
      id
      hash
      timestamp
    }
    memberships: authorityMembershipsByEpochId {
      totalCount
    }
  }
`);

gql(/* GraphQL */ `
  fragment EpochParticipation on Epoch {
    validatorMemberships: authorityMembershipsByEpochId(
      filter: {
        validatorByValidatorId: { accountByAccountId: { idSs58: { equalTo: $validatorIdSs58 } } }
      }
    ) @include(if: $withValidator) {
      totalCount
    }
    operatorEpochs: operatorEpochesByEpochId(
      filter: {
        operatorByOperatorId: { accountByAccountId: { idSs58: { equalTo: $operatorIdSs58 } } }
      }
    ) @include(if: $withOperator) {
      totalCount
    }
  }
`);

export const epochCastFields = ['bond', 'totalBonded'] as const;
export type CastEpochFragment = TokenCast<EpochFragment, (typeof epochCastFields)[number]>;

gql(/* GraphQL */ `
  fragment AuthorityMembershipWithValidator on AuthorityMembership {
    id
    bid
    reward
    rewardValueUsd
    validator: validatorByValidatorId {
      id
      account: accountByAccountId {
        id
        idSs58
        alias
        fundingEvents: accountFundingEventsByAccountId(condition: { epochId: $epochId }) {
          groupedAggregates(groupBy: TYPE) {
            sum {
              amount
              valueUsd
            }
            keys
          }
        }
      }
    }
  }
`);

export const authorityMembershipWithValidatorCastFields = ['bid', 'reward', 'amount'] as const;
export type CastAuthorityMembershipWithValidatorFragment = TokenCast<
  AuthorityMembershipWithValidatorFragment,
  (typeof authorityMembershipWithValidatorCastFields)[number]
>;
